import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/services/api";

// Priceable usage event types, in display order, with friendly labels.
const PRICEABLE = [
  { key: "envelope_sent", label: "Envelope sent" },
  { key: "envelope_completed", label: "Envelope completed" },
  { key: "sms_sent", label: "SMS sent" },
  { key: "email_sent", label: "Email sent" },
  { key: "api_call", label: "API call (overage)" },
  { key: "storage_used", label: "Storage (per byte)" },
  { key: "template_created", label: "Template created" },
  { key: "bulk_send", label: "Bulk send" },
  { key: "signing_link_created", label: "Signing link" },
  { key: "in_person_session", label: "In-person session" },
];

/**
 * Admin editor for an organisation's per-unit usage prices (manual/invoice
 * billing). Saving re-rates the org's unbilled usage-log rows to the new prices.
 */
export const UsagePricesEditor = ({
  organisationId,
  organisationName,
  onUpdate,
}) => {
  const queryClient = useQueryClient();
  const [values, setValues] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "usage-prices", organisationId],
    queryFn: async () => {
      const response = await api.get(
        `/admin/organisations/${organisationId}/usage-prices`,
      );
      return response.data?.data;
    },
    enabled: !!organisationId,
  });

  // Seed the editable fields from the effective prices once loaded.
  useEffect(() => {
    if (data?.prices) {
      const seeded = {};
      for (const { key } of PRICEABLE) {
        seeded[key] = String(data.prices[key] ?? 0);
      }
      setValues(seeded);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: async (prices) => {
      const response = await api.put(
        `/admin/organisations/${organisationId}/usage-prices`,
        { prices },
      );
      return response.data?.data;
    },
    onSuccess: () => {
      toast.success("Usage prices updated");
      queryClient.invalidateQueries({
        queryKey: ["admin", "usage-prices", organisationId],
      });
      // Refresh the Usage Log / unbilled summary, whose amounts just changed.
      queryClient.invalidateQueries({ queryKey: ["admin", "usage-log"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "unbilled-usage"] });
      onUpdate?.();
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.error?.message || "Failed to update usage prices",
      );
    },
  });

  const handleSave = () => {
    const prices = {};
    for (const { key } of PRICEABLE) {
      const num = Number(values[key]);
      if (!Number.isFinite(num) || num < 0) {
        toast.error(`Invalid price for ${key}`);
        return;
      }
      prices[key] = num;
    }
    mutation.mutate(prices);
  };

  const isInvoiceMode = data?.billingMode === "invoice";

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle>Usage prices</CardTitle>
        <CardDescription>
          Per-unit prices used to rate this organisation&apos;s usage events.
          Saving re-rates all unbilled Usage Log rows to the new prices (billed
          rows keep their invoiced price).
          {!isInvoiceMode && data && (
            <span className="mt-1 block text-amber-600">
              This organisation is on <strong>{data.billingMode}</strong>{" "}
              billing — usage prices mainly apply to manual (invoice) billing.
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              {PRICEABLE.map(({ key, label }) => (
                <div key={key} className="space-y-1.5">
                  <Label htmlFor={`price-${key}`} className="text-sm">
                    {label}
                  </Label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      $
                    </span>
                    <Input
                      id={`price-${key}`}
                      type="number"
                      min="0"
                      step="0.001"
                      className="pl-7 font-mono"
                      value={values[key] ?? ""}
                      onChange={(e) =>
                        setValues((v) => ({ ...v, [key]: e.target.value }))
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
              <Button onClick={handleSave} disabled={mutation.isPending}>
                {mutation.isPending
                  ? "Saving…"
                  : `Save prices${organisationName ? ` for ${organisationName}` : ""}`}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default UsagePricesEditor;
